// src/pages/Expense.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Tabs,
    Tab,
    Avatar,
    Alert,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    ListItemIcon,
    ListItemText
} from '@mui/material';

import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    AttachMoney,
    Category,
    Description,
    CalendarMonth,
    CheckCircle,
    Cancel,
    Pending,
    Receipt,
    Assessment,
    TrendingUp,
    TrendingDown,
    AccountBalance,
    MoneyOff,
    Money,
    LocalAtm,
    Savings,
    ViewModule,
    ViewList,
    History,
    Note,
    EventNote,
    Build,
    People,
    VerifiedUser,
    Apartment,
    MoreVert
} from '@mui/icons-material';

import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIExpenseList,
    APIExpenseStore,
    APIExpenseUpdate,
    APIExpenseDelete,
    APIExpenseExportExcel,
    APIExpenseExportPDF,
    APIExpensePrint,
    APIExpenseStats
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Expense = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedExpenseForAction, setSelectedExpenseForAction] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('list');
    const [statistics, setStatistics] = useState(null);

    // Form data for Expense
    const [formData, setFormData] = useState({
        name: '',
        category: 'other',
        description: '',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        remarks: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        category: '',
        status: '',
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        category: false,
        amount: false,
        expense_date: false,
        status: false
    });

    // Category options
    const categoryOptions = [
        { id: 'maintenance', name: 'Maintenance', color: 'primary', icon: <Build /> },
        { id: 'staff', name: 'Staff', color: 'secondary', icon: <People /> },
        { id: 'salaries', name: 'Salaries', color: 'success', icon: <Money /> },
        { id: 'inventory', name: 'Inventory', color: 'error', icon: <Category /> },
        { id: 'operating_costs', name: 'Operating Costs', color: 'warning', icon: <AccountBalance /> },
        { id: 'insurance', name: 'Insurance', color: 'info', icon: <VerifiedUser /> },
        { id: 'equipment', name: 'Equipment', color: 'default', icon: <Build /> },
        { id: 'other', name: 'Other', color: 'default', icon: <Category /> },
        { id: 'rent', name: 'Rent', color: 'primary', icon: <Apartment /> },
        { id: 'utilities', name: 'Utilities', color: 'secondary', icon: <LocalAtm /> },
        { id: 'marketing', name: 'Marketing', color: 'success', icon: <TrendingUp /> }
    ];

    // Status options
    const statusOptions = [
        { id: 'pending', name: 'Pending', color: 'warning', icon: <Pending /> },
        { id: 'approved', name: 'Approved', color: 'success', icon: <CheckCircle /> },
        { id: 'rejected', name: 'Rejected', color: 'error', icon: <Cancel /> }
    ];

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Use the updated permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchStatistics();
    }, [showToast]);

    // Fetch expenses
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIExpenseList);

            if (result.success) {
                const expenses = Array.isArray(result.data) ? result.data : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: expenses
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIExpenseStats);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print expense details", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APIExpensePrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export expense data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.category) formDataToSend.append('category', filters.category);
            if (filters.status) formDataToSend.append('status', filters.status);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APIExpenseExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export expense data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.category) formDataToSend.append('category', filters.category);
            if (filters.status) formDataToSend.append('status', filters.status);
            
            const result = await apiService.exportToPDF(APIExpenseExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(expense => {
        const matchesSearch = searchTerm === '' ||
            expense.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.remarks?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesName = !filters.name ||
            expense.name?.toLowerCase().includes(filters.name.toLowerCase());

        const matchesCategory = !filters.category ||
            expense.category === filters.category;

        const matchesStatus = !filters.status ||
            expense.status === filters.status;

        let matchesDate = true;
        if (filters.dateFrom) {
            const expenseDate = new Date(expense.expense_date).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && expenseDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const expenseDate = new Date(expense.expense_date).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && expenseDate <= filterToDate;
        }

        let matchesAmount = true;
        if (filters.amountFrom) {
            matchesAmount = matchesAmount && parseFloat(expense.amount) >= parseFloat(filters.amountFrom);
        }

        if (filters.amountTo) {
            matchesAmount = matchesAmount && parseFloat(expense.amount) <= parseFloat(filters.amountTo);
        }

        return matchesSearch && matchesName && matchesCategory && matchesStatus && matchesDate && matchesAmount;
    });

    // Calculate totals from statistics
    const calculateTotals = () => {
        if (statistics) {
            return {
                total: statistics.total_expenses || 0,
                pending: statistics.pending_expenses || 0,
                approved: statistics.approved_expenses || 0,
                rejected: statistics.rejected_expenses || 0
            };
        }
        
        const total = filteredData.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        const pending = filteredData
            .filter(expense => expense.status === 'pending')
            .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        const approved = filteredData
            .filter(expense => expense.status === 'approved')
            .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        const rejected = filteredData
            .filter(expense => expense.status === 'rejected')
            .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);

        return { total, pending, approved, rejected };
    };

    // Calculate filtered totals - History.js style
    const calculateFilteredTotals = () => {
        const totalAmount = filteredData.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        const pendingAmount = filteredData
            .filter(expense => expense.status === 'pending')
            .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        const approvedAmount = filteredData
            .filter(expense => expense.status === 'approved')
            .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        const rejectedAmount = filteredData
            .filter(expense => expense.status === 'rejected')
            .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
        
        const pendingCount = filteredData.filter(expense => expense.status === 'pending').length;
        const approvedCount = filteredData.filter(expense => expense.status === 'approved').length;
        const rejectedCount = filteredData.filter(expense => expense.status === 'rejected').length;
        
        // Get unique categories
        const uniqueCategories = new Set(filteredData.map(e => e.category)).size;

        return {
            totalAmount,
            pendingAmount,
            approvedAmount,
            rejectedAmount,
            pendingCount,
            approvedCount,
            rejectedCount,
            uniqueCategories,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add expenses", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            name: '',
            category: 'other',
            description: '',
            amount: '',
            expense_date: new Date().toISOString().split('T')[0],
            status: 'pending',
            remarks: ''
        });
        setErrors({
            name: false,
            category: false,
            amount: false,
            expense_date: false,
            status: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
        handleActionMenuClose();
    };

    const handleCloseView = () => {
        setOpenView(false);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit expenses", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            name: record.name || '',
            category: record.category || 'other',
            description: record.description || '',
            amount: record.amount || '',
            expense_date: record.expense_date ? record.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
            status: record.status || 'pending',
            remarks: record.remarks || ''
        });
        setOpenAdd(true);
        handleActionMenuClose();
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete expenses", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
        handleActionMenuClose();
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        if (name === 'amount') {
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    [name]: true
                }));
                showToast("Input Error", "Please enter valid numbers only", "error");
                return;
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            dateFrom: firstDay,
            dateTo: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            category: '',
            status: '',
            dateFrom: '',
            dateTo: '',
            amountFrom: '',
            amountTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const validateForm = () => {
        if (!formData.name?.trim()) {
            showToast("Validation Error", "Expense name is required", "warning");
            return false;
        }
        if (!formData.category) {
            showToast("Validation Error", "Category is required", "warning");
            return false;
        }
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            showToast("Validation Error", "Amount is required and must be greater than 0", "warning");
            return false;
        }
        if (!formData.expense_date) {
            showToast("Validation Error", "Expense date is required", "warning");
            return false;
        }
        if (!formData.status) {
            showToast("Validation Error", "Status is required", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add expenses", "error");
            return;
        }

        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update expenses", "error");
            return;
        }

        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete expenses", "error");
            return;
        }

        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('category', formData.category);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('amount', formData.amount);
            formDataToSend.append('expense_date', formData.expense_date);
            formDataToSend.append('status', formData.status);
            formDataToSend.append('remarks', formData.remarks || '');
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIExpenseStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIExpenseUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIExpenseDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, expense) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedExpenseForAction(expense);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedExpenseForAction(null);
    };

    // Helper functions
    const getStatusColor = (status) => {
        const option = statusOptions.find(opt => opt.id === status);
        return option ? option.color : 'default';
    };

    const getStatusIcon = (status) => {
        const option = statusOptions.find(opt => opt.id === status);
        return option ? option.icon : null;
    };

    const getStatusText = (status) => {
        const option = statusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const getCategoryColor = (category) => {
        const option = categoryOptions.find(opt => opt.id === category);
        return option ? option.color : 'default';
    };

    const getCategoryIcon = (category) => {
        const option = categoryOptions.find(opt => opt.id === category);
        return option ? option.icon : <Category />;
    };

    const getCategoryText = (category) => {
        const option = categoryOptions.find(opt => opt.id === category);
        return option ? option.name : category;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const totals = calculateTotals();

    // Render grid view
    const renderGridView = () => {
        if (viewMode !== 'grid') return null;

        const currentData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        if (currentData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Receipt sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No expenses found
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ p: 2 }}>
                {currentData.map((record) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                        <Card sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': { boxShadow: 6 }
                        }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                        {getCategoryIcon(record.category)}
                                    </Avatar>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            icon={getStatusIcon(record.status)}
                                            label={getStatusText(record.status)}
                                            color={getStatusColor(record.status)}
                                            size="small"
                                        />
                                        <IconButton 
                                            size="small" 
                                            sx={{ color: theme.palette.action.main }}
                                            onClick={(e) => handleActionMenuOpen(e, record)}
                                            aria-label="actions"
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Typography variant="h6" component="div" gutterBottom>
                                    {record.name}
                                </Typography>
                                <Chip
                                    label={getCategoryText(record.category)}
                                    color={getCategoryColor(record.category)}
                                    size="small"
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="h6" sx={{ mt: 2, color: theme.palette.error.main }}>
                                    {formatCurrency(record.amount)}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                    <CalendarMonth fontSize="small" /> {new Date(record.expense_date).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Expense Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Expense Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Expenses</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {formatCurrency(totals.total)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                <MoneyOff sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Pending</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                    {formatCurrency(totals.pending)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                <Pending sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Approved</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {formatCurrency(totals.approved)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                <CheckCircle sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Rejected</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                    {formatCurrency(totals.rejected)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.error.light, width: 56, height: 56 }}>
                                <Cancel sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Additional Statistics from API */}
            {statistics && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Total Count</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {statistics.expense_count || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">This Month</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                {formatCurrency(statistics.this_month_expenses || 0)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Average Expense</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                                {formatCurrency(statistics.average_expense || 0)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Pending Count</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                {statistics.pending_count || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Expense Management ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Add Expense
                            </Button>
                        )}

                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchData();
                                fetchStatistics();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewList />
                            </ToggleButton>
                            <ToggleButton value="grid">
                                <ViewModule />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search expenses..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.category || filters.status || filters.dateFrom || filters.dateTo || filters.amountFrom || filters.amountTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.category && (
                            <Chip
                                label={`Category: ${getCategoryText(filters.category)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, category: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusText(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color={getStatusColor(filters.status)}
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.amountFrom && (
                            <Chip
                                label={`Amount ≥ ${formatCurrency(filters.amountFrom)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, amountFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.amountTo && (
                            <Chip
                                label={`Amount ≤ ${formatCurrency(filters.amountTo)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, amountTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedExpenseForAction && handleOpenView(selectedExpenseForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedExpenseForAction && handlePrint(selectedExpenseForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('EDIT') && (
                    <MenuItem onClick={() => selectedExpenseForAction && handleOpenEdit(selectedExpenseForAction)}>
                        <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Edit</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('DELETE') && (
                    <MenuItem onClick={() => selectedExpenseForAction && handleOpenDelete(selectedExpenseForAction)}>
                        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* List View */}
                {viewMode === 'list' && (
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Expense Name</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell align="right">Amount (UGX)</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created Date</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                        <TableRow
                                            key={record.id || index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    {new Date(record.expense_date).toLocaleDateString()}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {record.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getCategoryIcon(record.category)}
                                                    label={getCategoryText(record.category)}
                                                    color={getCategoryColor(record.category)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                    {formatCurrency(record.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(record.status)}
                                                    label={getStatusText(record.status)}
                                                    color={getStatusColor(record.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(record.created_at)}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Actions">
                                                    <IconButton 
                                                        sx={{ color: theme.palette.action.main }}
                                                        onClick={(e) => handleActionMenuOpen(e, record)}
                                                        size="small"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                
                                {/* Footer with Totals - History.js Style */}
                                {filteredData.length > 0 && (
                                    <TableBody>
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.primary.light + '20',
                                            borderTop: '3px solid',
                                            borderTopColor: theme.palette.primary.main,
                                            '& td': {
                                                fontWeight: 'bold',
                                                py: 2.5
                                            }
                                        }}>
                                            <TableCell colSpan={2} align="right">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Totals ({filteredTotals.totalCount} expenses):
                                                </Typography>
                                            </TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<Category />}
                                                    label={`${filteredTotals.uniqueCategories} Categories`}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                        {formatCurrency(filteredTotals.totalAmount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total Amount
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        icon={<Pending />}
                                                        label={`${formatCurrency(filteredTotals.pendingAmount)}`}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label={`${formatCurrency(filteredTotals.approvedAmount)}`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label={`${formatCurrency(filteredTotals.rejectedAmount)}`}
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell colSpan={2}>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        icon={<Pending />}
                                                        label={`${filteredTotals.pendingCount} Pending`}
                                                        size="small"
                                                        color="warning"
                                                    />
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label={`${filteredTotals.approvedCount} Approved`}
                                                        size="small"
                                                        color="success"
                                                    />
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label={`${filteredTotals.rejectedCount} Rejected`}
                                                        size="small"
                                                        color="error"
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && renderGridView()}

                {filteredData.length === 0 && viewMode === 'list' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 8,
                        color: 'text.secondary'
                    }}>
                        <Receipt sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">No expenses found</Typography>
                        <Typography variant="body2">Try adjusting your search query</Typography>
                    </Box>
                )}
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Expenses
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Expenses
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show This Month's Expenses
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <TextField
                            fullWidth
                            label="Expense Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                name="category"
                                value={filters.category}
                                onChange={handleFilterChange}
                                label="Category"
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categoryOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {item.icon}
                                            {item.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {item.icon}
                                            {item.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Amount Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Min Amount"
                                    type="number"
                                    name="amountFrom"
                                    value={filters.amountFrom}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Max Amount"
                                    type="number"
                                    name="amountTo"
                                    value={filters.amountTo}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Expense Dialog */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Expense" : "Edit Expense"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    Expense Information
                                </Typography>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Expense Name *"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Description />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Category *</InputLabel>
                                    <Select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        label="Category *"
                                    >
                                        {categoryOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.icon}
                                                    {item.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description (Optional)"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Note />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Amount (UGX) *"
                                    name="amount"
                                    type="number"
                                    value={formData.amount}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Expense Date *"
                                    name="expense_date"
                                    type="date"
                                    value={formData.expense_date}
                                    onChange={handleInputChange}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarMonth />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Status *</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        label="Status *"
                                    >
                                        {statusOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.icon}
                                                    {item.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Remarks (Optional)"
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={2}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EventNote />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                {postStatus === "ADD" ? "Save Expense" : "Update Expense"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Expense Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Description />} iconPosition="start" />
                                <Tab label="Financial" icon={<AttachMoney />} iconPosition="start" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" color="primary">
                                                {selectedRecord.name}
                                            </Typography>
                                            <Chip
                                                icon={getStatusIcon(selectedRecord.status)}
                                                label={getStatusText(selectedRecord.status)}
                                                color={getStatusColor(selectedRecord.status)}
                                            />
                                        </Box>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Category:</b>{' '}
                                            <Chip
                                                icon={getCategoryIcon(selectedRecord.category)}
                                                label={getCategoryText(selectedRecord.category)}
                                                color={getCategoryColor(selectedRecord.category)}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Description:</b> {selectedRecord.description || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    {selectedRecord.remarks && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                <b>Remarks:</b> {selectedRecord.remarks}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Amount:</b>{' '}
                                            <Chip
                                                label={formatCurrency(selectedRecord.amount)}
                                                color="error"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Expense Date:</b> {new Date(selectedRecord.expense_date).toLocaleDateString()}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Status:</b>{' '}
                                            <Chip
                                                icon={getStatusIcon(selectedRecord.status)}
                                                label={getStatusText(selectedRecord.status)}
                                                color={getStatusColor(selectedRecord.status)}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                </Box>
                            )}

                            {/* System Information - Card Style */}
                                <SystemInformation
                                    createdBy={selectedRecord.created_by}
                                    updatedBy={selectedRecord.updated_by}
                                    createdAt={selectedRecord.created_at}
                                    updatedAt={selectedRecord.updated_at}
                                    title="Expense System Information"
                                />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Expense</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this expense?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name}
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    icon={getCategoryIcon(selectedRecord.category)}
                                    label={`${formatCurrency(selectedRecord.amount)} - ${getCategoryText(selectedRecord.category)}`}
                                    color="error"
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                        Warning: This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Expense;